import {
  Avatar,
  AvatarBadge,
  Box,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { ReactElement, FC } from 'react'
import { useColor } from '../../theme'
import { RunningTextLabel } from '../RunningTextLabel'
import { PersonaAvailability } from './types'
import { useAvailabilityColor } from './useAvailabilityColor'
import {
  getAvailabilityBadgeMargin,
  getAvailabilityBadgeSize,
  getAvailabilityBadgeBorder,
  getAvatarSize,
  getHorizontalSpacing,
  getVerticalSpacing,
  SELECTION_WIDTH,
  PersonaSize,
  getTitleFontWeight
} from './avatarSizeHelpers'

// /**
//  * Persona status.
//  * Should mimic the backend since we don't want to rely on the SDK types here.
//  */

export interface IPersonaProps {
  availability?: PersonaAvailability
  avatarBgColor?: string
  avatarUrl?: string
  description?: ReactElement | string | null | undefined
  isActive?: boolean
  isCompact?: boolean
  isSelected?: boolean
  onClick?: () => void
  size?: PersonaSize
  title?: ReactElement | string
  id?: string
  username: string
  hasDescriptionEllipsis?: boolean
  maxTitleWidth?: number | string
  maxDescriptionWidth?: number | string
  descriptionFontWeight?: string
}

/**
 * Persona component.
 *
 * Available in three sizes: 'small' | 'medium' | 'large'.
 *
 * Invalid or non-provided `avatarUrl` property will result on the display of
 * the username initials.
 *
 * The `title` property can be used to replace the default username.
 *
 * To strip the `username` (or `title`) and the `description` use the
 * `isCompact` boolean property.
 *
 * To highlight it, use the `isSelected` boolean property.
 *
 */
export const Persona: FC<IPersonaProps> = ({
  availability,
  avatarBgColor,
  avatarUrl,
  description,
  isActive,
  isCompact = false,
  isSelected = false,
  onClick,
  size = PersonaSize.XSmall,
  title,
  username,
  id,
  hasDescriptionEllipsis = false,
  maxTitleWidth,
  maxDescriptionWidth,
  descriptionFontWeight,
}) => {
  const descriptionColor = useColor({ nuance: 0, palette: 'text' })
  const selectionColor = useColor({ nuance: 1, palette: 'secondary' })
  const usernameColor = useColor({ nuance: 5, palette: 'text' })

  const avatarSize = getAvatarSize(size)

  const availabilityBadgeMargin = getAvailabilityBadgeMargin(size, isSelected)
  const availabilityBadgeSize = getAvailabilityBadgeSize(size)
  const availabilityBadgeBorder = getAvailabilityBadgeBorder(size)
  const usernameFontSize = size === PersonaSize.Small || size === PersonaSize.XSmall ? 'md' : 'xl'
  const horizontalSpacing = getHorizontalSpacing(size)
  const verticalSpacing = getVerticalSpacing(size)
  const titleFontweight = getTitleFontWeight(size)

  const { getAvailabilityBadgeColor } = useAvailabilityColor()

  const DescriptionComponent = hasDescriptionEllipsis ? RunningTextLabel : Text

  return (
    <HStack spacing={horizontalSpacing}>
      <Box
        background="transparent"
        borderColor={selectionColor}
        borderRadius="full"
        boxSize={avatarSize}
        data-testid="persona-avatar"
        minBlockSize={avatarSize}
        minWidth={avatarSize}
        {...(isSelected
          ? {
            borderWidth: SELECTION_WIDTH,
            padding: SELECTION_WIDTH,
          }
          : {})}
        {...(onClick ? { cursor: 'pointer', onClick } : {})}>
        <Avatar
          bgColor={avatarBgColor}
          boxSize="100%"
          id={id}
          name={username}
          src={avatarUrl}>
          {availability && (
            <AvatarBadge
              backgroundColor={getAvailabilityBadgeColor(availability)}
              borderWidth={availabilityBadgeBorder}
              boxSize={availabilityBadgeSize}
              margin={availabilityBadgeMargin}
            />
          )}
        </Avatar>
      </Box>
      {!isCompact && (
        <VStack alignItems="start" spacing={verticalSpacing}>
          <DescriptionComponent
            as="h2"
            color={usernameColor}
            data-pii=""
            data-testid="persona-username"
            display="inline"
            fontSize={usernameFontSize}
            fontWeight={titleFontweight}
            maxWidth={maxTitleWidth}>
            {title ?? username}
          </DescriptionComponent>
          {description && (
            <DescriptionComponent
              color={isActive ? usernameColor : descriptionColor}
              data-pii=""
              data-testid="persona-description"
              fontSize="sm"
              fontWeight={
                descriptionFontWeight ? descriptionFontWeight : 'medium'
              }
              maxWidth={maxDescriptionWidth}>
              {description}
            </DescriptionComponent>
          )}
        </VStack>
      )}
    </HStack>
  )
}
